/**
 * @component InnDatatableSsrComponent
 * @description Dual-mode datatable component compatible with inn-datatable npm package
 *
 * This component supports both client-side and server-side modes:
 * - CLIENT MODE (default): Works exactly like inn-datatable with client-side pagination/sorting
 * - SERVER MODE: Supports server-side pagination for large datasets
 *
 * @example CLIENT MODE (Default - Backward Compatible)
 * ```typescript
 * // Works exactly like inn-datatable - no changes needed
 * import { DataTableTypes } from "inn-datatable";
 *
 * dataTable = {
 *   dataChangedObs: new BehaviorSubject(null),
 *   heads: <DataTableTypes.Head[]>[
 *     { title: "Name", key: "name" },
 *     { title: "Email", key: "email" }
 *   ],
 *   rows: [...], // All data loaded
 *   options: <DataTableTypes.Options>{
 *     noOfRowsToDisplay: 10,
 *     singleActions: [{ title: "View" }]
 *   }
 * };
 * ```
 *
 * ```html
 * <!-- Client-side mode (default) -->
 * <inn-datatable-ssr
 *   [heads]="dataTable.heads"
 *   [bodyrows]="dataTable.rows"
 *   [options]="dataTable.options"
 *   [dataChanged]="dataTable.dataChangedObs">
 * </inn-datatable-ssr>
 * ```
 *
 * @example SERVER MODE (Opt-In for SSR)
 * ```typescript
 * pagination = {
 *   currentPage: 1,
 *   pageSize: 10,
 *   totalItems: 100,
 *   totalPages: 10
 * };
 *
 * onPaginationChange(event: any) {
 *   this.pagination = event;
 *   this.fetchDataFromServer(event.currentPage, event.pageSize);
 * }
 * ```
 *
 * ```html
 * <!-- Server-side mode (opt-in) -->
 * <inn-datatable-ssr
 *   [mode]="'server'"
 *   [heads]="dataTable.heads"
 *   [bodyrows]="dataTable.rows"
 *   [options]="dataTable.options"
 *   [pagination]="pagination"
 *   (paginationChange)="onPaginationChange($event)">
 * </inn-datatable-ssr>
 * ```
 */

import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, TemplateRef, OnChanges, SimpleChanges } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, Subscription, BehaviorSubject } from "rxjs";
import { switchMap, catchError, finalize } from "rxjs/operators";
import { of } from "rxjs";
import { exportTableToCSV, exportTableToCSV2 } from "src/app/utils/utils";

export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export type PaginationType = 'simple' | 'numbered' | 'extended';
export type DataTableMode = 'client' | 'server';

export interface PaginationConfig {
  type?: PaginationType;
  maxPageButtons?: number;
}

@Component({
  selector: "app-datatable",
  templateUrl: "./inn-datatable-ssr.component.html",
  styleUrls: ["./inn-datatable-ssr.component.css"],
})
export class InnDatatableSsrComponent implements OnInit, OnDestroy, OnChanges {
  // Styling inputs (compatible with inn-datatable)
  @Input() tableContainerClass: string = "";
  @Input() tableClass: string = "table table-striped table-hover table-bordered table-sm";

  // Data inputs (compatible with inn-datatable)
  @Input() heads: any[] = [];
  @Input() bodyrows: any[] = [];
  @Input() options: any = {};
  @Input() dataChanged?: Observable<any>;

  // Mode: 'client' (default, backward compatible) or 'server' (opt-in SSR)
  @Input() mode: DataTableMode = 'client';

  // Server-side pagination inputs (optional in client mode)
  @Input() pagination?: PaginationMeta;

  // Pagination configuration
  @Input() paginationConfig: PaginationConfig = {
    type: 'simple',
    maxPageButtons: 5
  };

  // Loading state input
  @Input() loading: boolean = false;

  // Smart Mode inputs (when provided, component manages data fetching)
  @Input() apiUrl?: string; // When set, activates Smart Mode
  @Input() externalFilters?: any; // Additional filters to merge with internal state// Optional separate URL for export
  @Input() exportOptions!: {
    serverFile: boolean;
    exportUrl?: string;
    columns: any[];
    fileName: string;
  }
  @Input() exludedExports: string[] = ["action"];

  // Outputs (compatible with inn-datatable)
  @Output() feedback: EventEmitter<any> = new EventEmitter<any>();

  // Server-side pagination outputs
  @Output() paginationChange: EventEmitter<PaginationMeta> = new EventEmitter<PaginationMeta>();

  // Smart Mode output for API errors
  @Output() apiError: EventEmitter<any> = new EventEmitter<any>();

  // Internal state
  refreshing = false;
  searchInput = "";
  selectedrows: any[] = [];
  pageSizeOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  // Client-side pagination state
  private clientPagination: PaginationMeta = {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  };

  // Filtered and sorted data for client mode
  private processedRows: any[] = [];

  // Smart Mode internal state
  private smartPagination: PaginationMeta = {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  };
  private smartSortColumn: string = '';
  private smartSortDirection: 'asc' | 'desc' | '' = '';
  private smartSearchTerm: string = '';
  private smartLoading: boolean = false;

  // Smart Mode RxJS stream for data fetching
  private refresh$ = new BehaviorSubject<void>(null as any);

  private subscriptions: Subscription[] = [];

  // Unique table identifier for checkboxes
  tableclass: string = "inn-ssr-" +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  // Cache for checkbox state
  private cache = {
    checkBoxHeadId: "",
  };

  // Date fields that should be formatted
  dateFields = {
    createdAt: true,
    updatedAt: true,
    createdDate: true,
    createdOn: true,
    endDate: true,
    paymentDueDate: true,
    closeDate: true,
  };

  // Sorting state
  headHash: any = {};
  debounceTimer: any;

  constructor(
    private http: HttpClient
  ) { }

  ngOnInit() {
    // Set initial page size from options if in client mode
    if (this.mode === 'client' && this.options && this.options.noOfRowsToDisplay) {
      this.clientPagination.pageSize = this.options.noOfRowsToDisplay;
    }

    // Smart Mode: Set up data fetching stream
    if (this.isSmartMode()) {
      this.initializeSmartMode();
    }

    // Subscribe to data changes (Manual/Client mode)
    if (this.dataChanged) {
      const sub = this.dataChanged.subscribe((changed) => {
        if (changed) {
          this.refreshing = true;
          setTimeout(() => {
            this.refreshing = false;
            this.initializeTable();
            if (this.mode === 'client' && !this.isSmartMode()) {
              this.updateClientPagination();
            }
            if (this.mode === 'server' && this.isSmartMode()) {
              this.refresh$.next(null as any);
            }
          }, 1000);
        }
      });
      this.subscriptions.push(sub);
    }

    this.initializeTable();

    // Initialize client pagination on first load (only if not Smart Mode)
    if (this.mode === 'client' && !this.isSmartMode()) {
      this.updateClientPagination();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detect external filter changes in Smart Mode
    if (this.isSmartMode() && changes['externalFilters'] && !changes['externalFilters'].firstChange) {
      this.refresh$.next(null as any);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Check if component is in Smart Mode
   */
  private isSmartMode(): boolean {
    return !!this.apiUrl;
  }

  /**
   * Initialize Smart Mode data fetching stream
   */
  private initializeSmartMode() {
    // Set initial page size from options
    if (this.options && this.options.noOfRowsToDisplay) {
      this.smartPagination.pageSize = this.options.noOfRowsToDisplay;
    }

    // Set up RxJS stream with switchMap to prevent race conditions
    const fetchSub = this.refresh$.pipe(
      switchMap(() => this.fetchSmartData())
    ).subscribe({
      next: (response: any) => {
        this.handleSmartDataResponse(response.response || response.data || {});
      },
      error: (error: any) => {
        this.smartLoading = false;
        this.apiError.emit(error);
        console.error('Smart Mode API Error:', error);
      }
    });

    this.subscriptions.push(fetchSub);
  }

  /**
   * Fetch data from API in Smart Mode
   */
  private fetchSmartData(): Observable<any> {
    if (!this.apiUrl) {
      return of({ data: [], total: 0, page: 1, limit: 10 });
    }

    this.smartLoading = true;

    // Build query parameters matching targets implementation
    let params = new HttpParams()
      .set('page', this.smartPagination.currentPage.toString())
      .set('limit', this.smartPagination.pageSize.toString());

    // Add search if present
    if (this.smartSearchTerm && this.smartSearchTerm.trim()) {
      params = params.set('search', this.smartSearchTerm.trim());
    }

    // Add sort if present
    if (this.smartSortColumn) {
      params = params.set('sort', this.smartSortColumn + ' ' + this.smartSortDirection);
    }

    // Merge external filters
    if (this.externalFilters) {
      Object.keys(this.externalFilters).forEach(key => {
        const value = this.externalFilters[key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get<any>(this.apiUrl, { params }).pipe(
      finalize(() => {
        this.smartLoading = false;
      }),
      catchError((error) => {
        this.smartLoading = false;
        this.apiError.emit(error);
        return of({ data: [], total: 0, page: 1, limit: this.smartPagination.pageSize });
      })
    );
  }

  /**
   * Handle Smart Mode API response
   */
  private handleSmartDataResponse(response: any) {
    // Update bodyrows with API data
    this.bodyrows = response.data || response.items || [];
    const pagination = response.pagination || {};

    // Update pagination from response (matching targets pattern)
    this.smartPagination = {
      currentPage: pagination.page || pagination.currentPage || 1,
      pageSize: pagination.limit || pagination.pageSize || 10,
      totalItems: pagination.total || pagination.totalItems || 0,
      totalPages: pagination.totalPages || Math.ceil((pagination.total || 0) / (pagination.limit || 10))
    };

    // Emit to data changed observable if it exists
    // if (this.dataChanged) {
    //   (this.dataChanged as BehaviorSubject<any>).next(true);
    // }
  }

  /**
   * Trigger Smart Mode data refresh
   */
  refreshSmartData() {
    if (this.isSmartMode()) {
      this.refresh$.next(null as any);
    }
  }

  /**
   * Initialize table heads for sorting
   */
  initializeTable() {
    if (this.heads) {
      this.heads.forEach((head) => {
        const old = this.headHash[head.key] ? this.headHash[head.key].sorted : false;
        this.headHash[head.key] = { sorted: old };
      });
    }
  }

  /**
   * Get effective pagination based on mode
   */
  get effectivePagination(): PaginationMeta {
    if (this.isSmartMode()) {
      return this.smartPagination;
    }
    if (this.mode === 'server' && this.pagination) {
      return this.pagination;
    }
    return this.clientPagination;
  }

  /**
   * Get display rows based on mode
   */
  get displayRows(): any[] {
    // Smart Mode: display API data directly (server handles pagination)
    if (this.isSmartMode()) {
      return this.bodyrows || [];
    }

    if (this.mode === 'server') {
      return this.bodyrows || [];
    }

    // Client mode: return paginated slice
    const start = (this.clientPagination.currentPage - 1) * this.clientPagination.pageSize;
    const end = start + this.clientPagination.pageSize;
    return this.processedRows.slice(start, end);
  }

  /**
   * Get effective loading state
   */
  get isLoading(): boolean {
    if (this.isSmartMode()) {
      return this.smartLoading;
    }
    return this.loading
  }

  /**
   * Update client-side pagination calculations
   */
  private updateClientPagination() {
    // Process rows (filtering, sorting)
    this.processedRows = this.getProcessedRows();

    // Calculate pagination
    const totalItems = this.processedRows.length;
    const totalPages = Math.ceil(totalItems / this.clientPagination.pageSize) || 1;

    this.clientPagination.totalItems = totalItems;
    this.clientPagination.totalPages = totalPages;

    // Ensure current page is valid
    if (this.clientPagination.currentPage > totalPages) {
      this.clientPagination.currentPage = totalPages;
    }
    if (this.clientPagination.currentPage < 1) {
      this.clientPagination.currentPage = 1;
    }
  }

  /**
   * Get processed rows (filtered and sorted) for client mode
   */
  private getProcessedRows(): any[] {
    let rows = [...(this.bodyrows || [])];

    // Apply search filter
    if (this.searchInput && this.searchInput.trim()) {
      const searchTerm = this.normalizeSearchValue(this.searchInput);
      const searchableKeys = (this.heads || [])
        .map(head => head.key)
        .filter(key => key && key !== 'checkbox');
      rows = rows.filter(row => {
        return searchableKeys.some(key => {
          let value = row[key];
          if (value == null) return false;
          const strVal = this.normalizeSearchValue(value);
          return strVal.includes(searchTerm);
        });
      });
    }

    // Apply sorting (if any column is sorted)
    const sortedHead = this.heads.find(head => this.headHash[head.key] && this.headHash[head.key].sorted);
    if (sortedHead) {
      const key = sortedHead.key;
      const ascending = this.headHash[key].sorted === 'asc';

      rows.sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        const comparison = aVal > bVal ? 1 : -1;
        return ascending ? comparison : -comparison;
      });
    }

    return rows;
  }

  private normalizeSearchValue(value: any): string {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[\u0024\u20a6\u20ac\u00a3\u00a5\u20b9,]/g, '');
  }

  /**
   * Handle header click events (checkbox or sorting)
   */
  headClicked(headData: any, refElement?: string) {
    switch (headData.key) {
      case "checkbox":
        if (refElement) {
          this.handleCheckboxHeadClick(refElement);
        }
        break;
      default:
        if (this.isSmartMode()) {
          // Smart Mode: handle sorting internally
          this.handleSmartSort(headData.key);
        } else if (this.mode === 'client') {
          // Client mode: perform client-side sorting
          this.handleClientSideSort(headData.key);
        } else {
          // Server mode: emit event for server-side sorting
          const direction = this.headHash[headData.key].sorted === 'asc' ? 'desc' : 'asc';
          this.headHash[headData.key].sorted = direction;
          this.feedback.emit({
            type: "sort",
            column: headData.key,
            direction
          });
        }
        break;
    }
  }

  /**
   * Handle client-side sorting
   */
  private handleClientSideSort(key: string) {
    // Toggle sort direction
    const currentSort = this.headHash[key].sorted;

    // Reset all other columns
    Object.keys(this.headHash).forEach(k => {
      this.headHash[k].sorted = false;
    });

    // Set new sort
    if (!currentSort || currentSort === 'desc') {
      this.headHash[key].sorted = 'asc';
    } else {
      this.headHash[key].sorted = 'desc';
    }

    // Update pagination with sorted data
    this.updateClientPagination();
  }

  /**
   * Handle Smart Mode sorting
   */
  private handleSmartSort(key: string) {
    // Toggle sort direction
    const currentSort = this.headHash[key].sorted;

    // Reset all other columns
    Object.keys(this.headHash).forEach(k => {
      this.headHash[k].sorted = false;
    });

    // Set new sort
    if (!currentSort || currentSort === 'desc') {
      this.headHash[key].sorted = 'asc';
      this.smartSortDirection = 'asc';
    } else {
      this.headHash[key].sorted = 'desc';
      this.smartSortDirection = 'desc';
    }

    this.smartSortColumn = key;

    // Trigger data refresh
    this.refresh$.next(null as any);
  }

  /**
   * Handle "select all" checkbox click
   */
  private handleCheckboxHeadClick(refElement: string) {
    this.cache.checkBoxHeadId = refElement;
    const checked = (document.querySelector("#" + refElement) as HTMLInputElement) && (document.querySelector("#" + refElement) as HTMLInputElement).checked;

    // Get all displayed rows for selection
    const selectableRows = this.displayRows.filter(row => !this.isCheckboxDisabled(row));

    document.querySelectorAll("." + this.tableclass + "-checkbox").forEach(
      (element: Element) => {
        const checkbox = element as HTMLInputElement;
        // Only toggle if not disabled
        if (!checkbox.disabled) {
          checkbox.checked = !checked;
        }
      }
    );

    if (checked) {
      this.selectedrows = [];
    } else {
      // Only select enabled rows
      this.selectedrows = [...selectableRows];
    }

    this.feedback.emit({ type: "selectedrows", data: this.selectedrows });
  }

  isSelected(row: any): boolean {
    if (!this.selectedrows || this.selectedrows.length === 0) return false;
    const firstkey = Object.keys(row)[0];
    return this.selectedrows.some((r) => r[firstkey] === row[firstkey]);
  }

  /**
   * Handle individual row checkbox change
   */
  itemChecked(data: any, checked: boolean) {
    if (checked) {
      this.selectedrows.push(data);
    } else {
      const firstkey = Object.keys(data)[0];
      this.selectedrows = this.selectedrows.filter(
        (rdata) => !(rdata[firstkey] === data[firstkey])
      );
    }
    this.feedback.emit({ type: "selectedrows", data: this.selectedrows });
    this.resetCheckBox();
  }

  /**
   * Clear all selected rows (parent-driven reset)
   */
  clearSelection() {
    this.selectedrows = [];
    this.feedback.emit({ type: "selectedrows", data: this.selectedrows });
    this.resetCheckBox();
  }

  /**
   * Reset checkbox head state
   */
  resetCheckBox() {
    if (this.cache.checkBoxHeadId) {
      const element = document.querySelector("#" + this.cache.checkBoxHeadId) as HTMLInputElement;
      if (element) {
        element.checked = false;
      }
    }
  }

  /**
   * Emit single action event
   */
  emitSingleAction(action: any, data: any) {
    this.feedback.emit({ type: "singleaction", action, data });
  }

  /**
   * Emit bulk action event
   */
  emitBulkAction(action: string) {
    this.feedback.emit({
      type: "bulkactions",
      action,
      data: this.selectedrows,
    });
  }

  /**
   * Emit view/edit actions on row click
   */
  emitViewActions(data: any, key: string) {
    if (key === "checkbox" || key === "action") {
      return;
    }
    if (this.options && this.options.emitClickActions) {
      this.options.emitClickActions.forEach((action: string) =>
        this.feedback.emit({ type: "singleaction", action, data })
      );
    }
  }

  /**
   * Handle page change
   */
  onPageChange(newPage: number) {
    if (this.isSmartMode()) {
      this.smartPagination.currentPage = newPage;
      this.refresh$.next(null as any);
      this.resetCheckBox();
    } else if (this.mode === 'client') {
      this.clientPagination.currentPage = newPage;
      this.resetCheckBox();
    } else {
      this.pagination = {
        currentPage: newPage,
        pageSize: this.pagination ? this.pagination.pageSize || 10 : 10,
        totalItems: this.pagination ? this.pagination.totalItems || 0 : 0,
        totalPages: this.pagination ? this.pagination.totalPages || 0 : 0
      };
      this.paginationChange.emit(this.pagination);
      this.resetCheckBox();
    }
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(newSize: number) {
    if (this.isSmartMode()) {
      this.smartPagination.pageSize = newSize;
      this.smartPagination.currentPage = 1;
      this.refresh$.next(null as any);
      this.resetCheckBox();
    } else if (this.mode === 'client') {
      this.clientPagination.pageSize = newSize;
      this.clientPagination.currentPage = 1;
      this.updateClientPagination();
      this.resetCheckBox();
    } else {
      const updatedPagination: PaginationMeta = {
        currentPage: 1,
        pageSize: newSize,
        totalItems: this.pagination ? this.pagination.totalItems || 0 : 0,
        totalPages: Math.ceil((this.pagination ? this.pagination.totalItems || 0 : 0) / newSize)
      };
      this.paginationChange.emit(updatedPagination);
      this.resetCheckBox();
    }
  }

  /**
   * Handle search input change
   */
  onSearch() {
    if (this.isSmartMode()) {
      // Smart Mode: update search term and trigger refresh with debounce
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.smartSearchTerm = this.searchInput.trim();
        this.smartPagination.currentPage = 1; // Reset to first page
        this.refresh$.next(null as any);
      }, 500);
    } else if (this.mode === 'client') {
      // Client mode: filter data client-side
      this.clientPagination.currentPage = 1; // Reset to first page
      this.updateClientPagination();
    } else {
      clearTimeout(this.debounceTimer);
      //need to debounce
      this.debounceTimer = setTimeout(() => {
        // Server mode: emit event
        this.feedback.emit({
          type: "search",
          searchTerm: this.searchInput.trim()
        });
      }, 500);
    }
  }

  /**
   * Parse and format date fields
   */
  parseDate(value: any): string {
    if (!value) {
      return "";
    }

    let dateValue = value;
    if (!parseInt(value)) {
      dateValue = (value + "").replace("WAT", "UTC");
    }

    try {
      return dateValue ? new Date(dateValue).toDateString() : "";
    } catch {
      return value;
    }
  }

  /**
   * Get minimum of two numbers (for display)
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Get starting item number for current page
   */
  get startItem(): number {
    const pag = this.effectivePagination;
    if (!pag.totalItems || pag.totalItems === 0) {
      return 0;
    }
    return (pag.currentPage - 1) * pag.pageSize + 1;
  }

  /**
   * Get ending item number for current page
   */
  get endItem(): number {
    const pag = this.effectivePagination;
    const calculated = pag.currentPage * pag.pageSize;
    return Math.min(calculated, pag.totalItems);
  }

  /**
   * Check if previous page button should be enabled
   */
  get hasPreviousPage(): boolean {
    return this.effectivePagination.currentPage > 1;
  }

  /**
   * Check if next page button should be enabled
   */
  get hasNextPage(): boolean {
    const pag = this.effectivePagination;
    return pag.currentPage < pag.totalPages;
  }

  /**
   * Navigate to previous page
   */
  previousPage() {
    if (this.hasPreviousPage) {
      this.onPageChange(this.effectivePagination.currentPage - 1);
    }
  }

  /**
   * Navigate to next page
   */
  nextPage() {
    if (this.hasNextPage) {
      this.onPageChange(this.effectivePagination.currentPage + 1);
    }
  }

  /**
   * Get page numbers to display based on pagination type
   */
  get pageNumbers(): (number | string)[] {
    const pag = this.effectivePagination;
    const { currentPage, totalPages } = pag;
    const maxButtons = this.paginationConfig.maxPageButtons || 5;

    if (totalPages <= maxButtons) {
      // Show all pages if total is less than max
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const halfMax = Math.floor(maxButtons / 2);

    // Always show first page
    pages.push(1);

    let startPage: number;
    let endPage: number;

    if (currentPage <= halfMax + 1) {
      // Near the beginning
      startPage = 2;
      endPage = maxButtons - 1;
    } else if (currentPage >= totalPages - halfMax) {
      // Near the end
      startPage = totalPages - maxButtons + 2;
      endPage = totalPages - 1;
    } else {
      // Middle
      startPage = currentPage - halfMax + 1;
      endPage = currentPage + halfMax - 1;
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push('...');
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }

  /**
   * Get extended page numbers (shows more pages)
   */
  get extendedPageNumbers(): (number | string)[] {
    const pag = this.effectivePagination;
    const { currentPage, totalPages } = pag;
    const maxButtons = Math.min(totalPages, 7); // Show up to 7 page buttons

    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 4) {
      // Near the beginning: show first 5 pages
      for (let i = 1; i <= Math.min(5, totalPages); i++) {
        pages.push(i);
      }
      if (totalPages > 5) {
        pages.push('...');
        pages.push(totalPages);
      }
    } else if (currentPage >= totalPages - 3) {
      // Near the end: show last 5 pages
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) {
        if (i > 1) pages.push(i);
      }
    } else {
      // Middle: show current and surrounding pages
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  }

  /**
   * Check if page number is current page
   */
  isCurrentPage(page: number | string): boolean {
    return page === this.effectivePagination.currentPage;
  }

  /**
   * Check if page is a valid number (not ellipsis)
   */
  isPageNumber(page: number | string): boolean {
    return typeof page === 'number';
  }

  /**
   * Navigate to specific page
   */
  goToPage(page: number | string) {
    if (typeof page === 'number' && page !== this.effectivePagination.currentPage) {
      this.onPageChange(page);
    }
  }

  /**
   * Export data in Smart Mode
   * @param format Export format (csv or excel)
   */
  exportData(format: 'csv' | 'excel' = 'csv') {
    if (!this.exportOptions) return;
    if (!this.isSmartMode()) {
      console.warn('Export is only available in Smart Mode (apiUrl must be provided)');
      return;
    }
    const url = this.exportOptions.exportUrl || this.apiUrl;

    if (!url) {
      console.warn('Export URL is not provided');
      return;
    }
    // Build query parameters matching current state
    let params = new HttpParams().set('export', format);
    // Add search if present
    if (this.smartSearchTerm && this.smartSearchTerm.trim()) {
      params = params.set('search', this.smartSearchTerm.trim());
    }
    // Add sort if present
    if (this.smartSortColumn) {
      params = params.set('sort', this.smartSortColumn + ' ' + this.smartSortDirection);
    }

    // Merge external filters
    if (this.externalFilters) {
      Object.keys(this.externalFilters).forEach(key => {
        const value = this.externalFilters[key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    // Make HTTP request for blob
    this.smartLoading = true;
    if (!this.exportOptions.serverFile) {
      this.http.get(url, { params }).subscribe((response: any) => {
        const data = response.response
        exportTableToCSV2(data, this.heads.filter((h: any) => !this.exludedExports.includes(h.key)), this.exportOptions.fileName);
        this.smartLoading = false;
      }, (error: any) => {
        console.error('Export error:', error);
        this.apiError.emit(error);
        this.smartLoading = false;
      })
    } else {
      this.http.get(url, { params, responseType: 'blob' }).subscribe({
        next: (blob: Blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Set filename based on format
          const timestamp = new Date().toISOString().split('T')[0];
          link.download = `export-${timestamp}.${format === 'excel' ? 'xlsx' : 'csv'}`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up
          window.URL.revokeObjectURL(url);
          this.smartLoading = false;
        },
        error: (error: any) => {
          console.error('Export error:', error);
          this.apiError.emit(error);
          this.smartLoading = false;
        }
      });
    }
  }

  /**
   * Check if checkbox should be disabled for a row
   * Supports both callback function and row property
   */
  isCheckboxDisabled(row: any): boolean {
    // Check row property first
    if (row && row.isCheckboxDisabled === true) {
      return true;
    }

    // Check options callback
    if (this.options && this.options.disableCheckbox && typeof this.options.disableCheckbox === 'function') {
      return this.options.disableCheckbox(row);
    }

    return false;
  }

  /**
   * Get template context for cell rendering
   */
  getCellTemplateContext(row: any, head: any): any {
    return {
      row: row,
      value: row[head.key],
      column: head
    };
  }

  /**
   * Check if head has a cell template
   */
  hasCellTemplate(head: any): boolean {
    return head && head.cellTemplate != null;
  }

  /**
   * Check if head has a header template
   */
  hasHeaderTemplate(head: any): boolean {
    return head && head.headerTemplate != null;
  }

  applyTransform(head: any, row: any): any {
    if (!head || typeof head.transform !== 'function') {
      return row[head.key];
    }
    try {
      return head.transform(row[head.key], row);
    } catch (error) {
      console.error(`Datatable transform failed for column "${head.key}"`, error);
      return row[head.key];
    }
  }

}
